import classNames from "classnames"
import React from "react"

import { useTranslation } from "@bounce/i18n"
import { useReferrals } from "@bounce/referrals"

import type { FeaturedPoiData } from "./StoreSlider"
import { StoreSliderCard } from "./StoreSliderCard"
import SafeLink from "../../Generic/SafeLink/SafeLink"
import { StoreItemListStructuredData } from "../../Generic/StructuredData/StoreItemListStructuredData"
import { useStoreSuggestions } from "../Store/hooks/useStoreSuggestions"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"
import { MAX_STORES_PER_GRID } from "@/screens/LuggageStorage/CityScreen/constants"
import type { PageType } from "@/types"

export type StoreGridProps = {
  className?: string
  from: PageType
  path: string
  searchQuery: string
  stores: NearbyStorageSpot[]
  featuredPoi?: FeaturedPoiData
  renderStructuredData?: boolean
  maxStores?: number
}

export const StoreGrid = ({
  className,
  from,
  searchQuery,
  stores,
  featuredPoi,
  renderStructuredData = true,
  path,
  maxStores = MAX_STORES_PER_GRID, // 3x3 grid (9 stores)
}: StoreGridProps) => {
  const { coupon: couponCode } = useReferrals.useCodes()
  const { handleClickStore, handleGetStoreUrl } = useStoreSuggestions({ couponCode, from, searchQuery })
  const displayedStores = stores.slice(0, maxStores)
  const { t } = useTranslation()

  return (
    <>
      <ul
        className={classNames("mt-6 grid grid-cols-1 gap-6 px-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-y-12", className)}>
        {displayedStores.map(store => (
          <li key={store.id} className="h-full">
            <SafeLink
              href={handleGetStoreUrl(store)}
              onClick={ev => handleClickStore(ev, store)}
              target="_self"
              title={t("cmp.storeGrid.anchorText", "Open {storeNickname} in Map View", {
                storeNickname: store.nickname,
              })}
              className="block h-full">
              <StoreSliderCard store={store} featuredPoi={featuredPoi} />
            </SafeLink>
          </li>
        ))}
      </ul>

      {renderStructuredData && <StoreItemListStructuredData path={path} stores={displayedStores} />}
    </>
  )
}
